<template>
  <app-dialog
    v-model="dialog"
    content-class="sourcery__dialog"
    width="100%">
    <!-- Dialog Content -->
    <v-card>
      <!-- Header -->
      <v-card-title class="d-flex gap-2 items-center align-center">
        <span class="justify-self-start">
          Project Views Manager
        </span>

        <div class="ml-12 d-flex gap-2">
          <v-icon color="grey darken-2">
            mdi-filter
          </v-icon>
          <v-select
            v-model="filteredRole"
            :items="roleOrder"
            label="Filter by Role"
            :menu-props="{ bottom: true, offsetY: true, contentClass: 'capitalize' }"
            clearable
            dense
            outlined
            hide-details
            style="max-width: 200px;" />
          <v-select
            v-model="filteredSpecialPermUser"
            :items="allUniqueSpecialPermUsers"
            label="Filter by User"
            :menu-props="{ bottom: true, offsetY: true }"
            clearable
            dense
            outlined
            hide-details
            style="width: 20rem;" />
        </div>

        <span
          class="sourcery__icon-wrapper black"
          @click="dialog = false">
          <v-icon
            color="white"
            size="15">
            mdi-close
          </v-icon>
        </span>
      </v-card-title>

      <!-- Rows -->
      <v-card-text>
        <div class="view-manager-wrapper">
          <div class="view-manager-row fs-14 font-weight-bold sticky">
            <div class="view-manager-row__view-name d-flex items-center align-center gap-4">
              <span>View Name</span>
              <v-btn
                class="white--text font-weight-medium text-none fs-14"
                color="blue"
                @click="showCopyPopup">
                Create New View
              </v-btn>
            </div>
            <div style="width: 40px;" />

            <div>
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <span
                    v-bind="attrs"
                    v-on="on">
                    Roles with Access to Views
                  </span>
                </template>
                <span>
                  Note: Creators have access to ALL views.
                </span>
              </v-tooltip>
              <div class="d-flex mt-2 gap-4">
                <v-tooltip
                  v-for="roleType in roleOrder"
                  :key="roleType"
                  right
                  max-width="220">
                  <template #activator="{ on, attrs }">
                    <span
                      class="text-capitalize"
                      style="width: 6rem;"
                      v-bind="attrs"
                      v-on="on">
                      {{ roleType }}
                    </span>
                  </template>
                  <span>
                    {{ roleTooltipText(roleType) }}
                  </span>
                </v-tooltip>
              </div>
            </div>

            <div>
              Guests Users with View Access
            </div>
          </div>
          <v-list
            style="max-height:100%; overflow-y: auto;">
            <div
              v-for="(view, index) in filteredViews"
              :key="index"
              class="view-manager-row">
              <div class="view-manager-row__view-name">
                <v-icon
                  v-if="isFilteredCustomView(view)"
                  class="mr-2"
                  color="grey">
                  mdi-filter-outline
                </v-icon>
                <span>{{ view && view.name }}</span>
              </div>
              <div class="d-flex gap-8">
                <app-icon-btn
                  v-if="viewIsDeletable(view)"
                  :access="deleteViewAccess"
                  :show-tooltip="!deleteViewAccess || !deleteViewAccess.allowed"
                  icon-color="black"
                  icon-height="20px"
                  icon-name="mdi-delete"
                  bottom
                  @click.stop="deleteViewAccess.allowed && deleteSelectedView(view)" />
                <div
                  v-else
                  style="width: 20px; height: 20px" />
                <app-icon-btn
                  :access="viewSettingsAccess"
                  :show-tooltip="!viewSettingsAccess || !viewSettingsAccess.allowed"
                  class="d-flex align-self-center"
                  icon-color="black"
                  icon-height="20px"
                  icon-name="mdi-settings"
                  bottom
                  @click.stop="viewSettingsAccess.allowed && showViewSettingsDialog(view)" />
              </div>
              <div>
                <div
                  v-if="isRolesLoadedForView(view)"
                  class="d-flex gap-4">
                  <v-tooltip
                    v-for="roleType in roleOrder"
                    :key="roleType"
                    bottom>
                    <template #activator="{ on, attrs }">
                      <div
                        class="d-flex justify-center "
                        style="width: 6rem;">
                        <v-checkbox
                          v-bind="attrs"
                          :input-value="roleHasAccessInView(roleType, view.id)"
                          hide-details
                          dense
                          class="ma-0 pa-0"
                          @change="onRoleAccessChanged(view, roleType, $event)"
                          v-on="on" />
                      </div>
                    </template>
                    <span class="text-capitalize">
                      {{ roleType }}
                    </span>
                  </v-tooltip>
                </div>
                <div
                  v-else
                  class="d-flex gap-4 w-100">
                  <v-skeleton-loader
                    v-for="i in 3"
                    :key="i"
                    :width="50"
                    type="text" />
                </div>
              </div>
              <div>
                <div v-if="isSpecialPermUsersLoadedForView(view)">
                  <div
                    class="px-4"
                    style="max-width: 24rem;">
                    <v-select
                      v-if="hasSpecialPermUsersForView(view)"
                      v-model="defaultSpecialPermUser[view.id]"
                      :items="getSpecialPermUsersForView(view)"
                      item-text="email"
                      item-value="id"
                      :item-disabled="() => true"
                      :menu-props="{ bottom: true, offsetY: true }"
                      hide-details
                      dense
                      outlined />
                  </div>
                </div>
                <div
                  v-else
                  class="d-flex justify-center items-center">
                  <v-skeleton-loader
                    :width="120"
                    type="text" />
                </div>
              </div>
            </div>
          </v-list>
        </div>
      </v-card-text>

      <!-- View Settings -->
      <ProjectDetailControlsView
        :item="viewForSettings"
        :query-information="queryInformation"
        :dialog-status.sync="showSettingsDialog"
        :initial-tab="initialControlsTab"
        :initial-selected-roles="initialSelectedViewRoles"
        :users-initial="usersInitial"
        :users-to-delete="usersToDelete"
        :roles-to-delete="rolesToDelete"
        @deleteView="deleteView"
        @viewUpdated="settingsViewUpdated"
        @rolesChanged="computeRemovedRoles"
        @userRemoved="addUserToDelete" />

      <!-- Create (actually copy) -->
      <ProjectDetailViewCopy
        :views="views"
        :selected-view="viewToCopy"
        :dialog-status.sync="showCopyDialog"
        @onCopy="onCopy"
        @updateCopiedView="setViewToCopy" />
    </v-card>
    <AppDialogCloser @click="dialog = false" />
  </app-dialog>
</template>

<script>
import {
  mapMutations,
  mapActions,
} from 'vuex';
import AppDialogCloser from '@/components/App/AppDialogCloser';
import ProjectDetailControlsView from '@/components/ProjectDetails/ProjectDetailsViews/ProjectDetailControlsView';
import ProjectDetailViewCopy from '@/components/ProjectDetails/ProjectDetailsViews/ProjectDetailViewCopy';
import { ProjectViewsControlsMixin } from '@/mixins/ProjectViewsControlsMixin';
import ProjectViewsApi from '@/services/graphql/projectViews';
import RequestAbortController from '@/utils/RequestAbortController';
import {
  COLLABORATOR_DESC,
  PERMISSION_DESCRIPTION,
} from '@/constants/userPermissions';

export default {
  name: 'ProjectViewsManager',
  components: {
    AppDialogCloser,
    ProjectDetailControlsView,
    ProjectDetailViewCopy,
  },
  mixins: [
    ProjectViewsControlsMixin,
  ],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    // Constants
    PERM_DESCS: [...PERMISSION_DESCRIPTION, COLLABORATOR_DESC],
    roleOrder: [
      'viewer',
      'collaborator',
      'editor',
    ],

    // Filter.
    filteredRole: '',
    filteredSpecialPermUser: null,

    // Dialog States.
    showSettingsDialog: false,
    showCopyDialog: false,

    // Fetched Data (TODO: Move to store)
    viewToRoles: {
    },
    viewToSpecialPerms: {
    },

    // First name to show in special perm guest dropdown each view
    defaultSpecialPermUser: {
    },
  }),
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    allViews() {
      return this.views ?? [];
    },
    filteredViews() {
      if (!this.filteredRole && !this.filteredSpecialPermUser) return this.allViews;

      return this.allViews.filter((v) => {
        if (this.filteredRole) {
          const roles = this.viewToRoles[v.id] ?? [];
          const hasFilteredRole = roles.some((item) => item.role === this.filteredRole);
          if (!hasFilteredRole) return false;
        }
        if (this.filteredSpecialPermUser) {
          const specialPermUsers = this.viewToSpecialPerms[v.id] ?? [];

          const hasFilteredSpecialPerm = specialPermUsers.some((item) => item.email === this.filteredSpecialPermUser);
          if (!hasFilteredSpecialPerm) return false;
        }

        return true;
      });
    },
    allUniqueSpecialPermUsers() {
      const uniqueUsers = new Map();
      Object.values(this.viewToSpecialPerms).forEach((users) => {
        users?.forEach((user) => {
          if (!uniqueUsers.has(user.id)) {
            uniqueUsers.set(user.id, user.email);
          }
        });
      });
      return [...uniqueUsers.values()];
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        this.fetchDataForAllViews();
      }
    },
    allViews(val, oldVal) {
      if (val && !oldVal) {
        this.fetchDataForAllViews(val);
      }
    },
  },
  created() {
    this.abortController = new RequestAbortController();
  },
  destroyed() {
    this.abortController.abort();
  },
  methods: {
    ...mapMutations({
      spinner: 'spinner',
    }),
    ...mapActions({
      handleError: 'handleError',
    }),

    /**
     * Data Fetching.
     */
    async fetchDataForAllViews(av) {
      const allViews = av || this.allViews;
      if (!allViews?.length) return;

      this.viewToRoles = {
      };
      this.viewToSpecialPerms = {
      };
      const viewIds = this.allViews.map((v) => v.id);

      let currIndex = 0;
      const worker = async () => {
        if (this.abortController?.aborted()) return;

        while (currIndex < viewIds.length) {
          const index = currIndex++;
          const id = viewIds[index];
          this.fetchRolesForView(id);
          this.fetchUsersWithSpecialPermsForView(id);
        }
      };

      const CONCURRENCY = 8;
      const workers = [];
      for (let i = 0; i < CONCURRENCY; i++) {
        workers.push(worker());
      }
      await Promise.all(workers);
    },
    async fetchRolesForView(id) {
      try {
        const { data } = await ProjectViewsApi.getRolesPermissionsToView(
          this.queryVariables(id),
          this.abortController,
        );
        const roles = data?.response;
        this.$set(this.viewToRoles, id, roles);

      } catch (err) {
        console.log(`Error fetching roles for view with id ${id}`, err);
      }
    },
    async fetchUsersWithSpecialPermsForView(id) {
      try {
        const { data } = await ProjectViewsApi.getUsersWithSpecialAccessToView(
          this.queryVariables(id),
          this.abortController,
        );
        const users = data?.response ?? [];
        this.$set(this.viewToSpecialPerms, id, users.map((item) => item.user));
        if (users?.length) {
          this.$set(this.defaultSpecialPermUser, id, users[0].user);
        }
      } catch (err) {
        console.log(`Error fetching roles for view with id ${id}`, err);
      }
    },

    /**
     * Callbacks
     */
    settingsViewUpdated() {
      this.updateViewForSettings();
      this.fetchRolesForView(this.viewForSettings?.id);
    },
    async onRoleAccessChanged(view, role, value) {
      const id = view.id;

      if (value) {
        const newRoles = [...this.viewToRoles[id], {
          role,
        }];
        this.$set(this.viewToRoles, id, newRoles);
        await ProjectViewsApi.grantPermissionForRoles({
          ...this.queryVariables(view.id),
          roles: newRoles.map((r) => r.role),
        });

      } else {
        const newRoles = this.viewToRoles[id].filter((r) => r.role !== role);
        this.$set(this.viewToRoles, id, newRoles);
        await ProjectViewsApi.deletePermissionForRoles({
          ...this.queryVariables(view.id),
          roles: [role],
        });
      }
    },

    /**
     * State helpers.
     */
    isFilteredCustomView(v) {
      return v && v.type === 'custom' && v.filterTag.Tag;
    },
    isRolesLoadedForView(v) {
      const roles = this.viewToRoles[v.id];
      if (roles) return true;
    },
    roleTooltipText(role) {
      const perm = this.PERM_DESCS.find((p) => p.value === role);
      return perm?.message ?? '';
    },
    roleHasAccessInView(r, viewId) {
      const roles = this.viewToRoles[viewId];
      return roles?.some((item) => item.role === r);
    },
    isSpecialPermUsersLoadedForView(v) {
      const users = this.viewToSpecialPerms[v.id];
      return Array.isArray(users);
    },
    hasSpecialPermUsersForView(v) {
      return this.getSpecialPermUsersForView(v)?.length;
    },
    getSpecialPermUsersForView(v) {
      return this.viewToSpecialPerms[v.id];
    },
    queryVariables(viewId) {
      return {
        projectId: this.projectId,
        tableId: this.scheduleId,
        tableType: 'schedule',
        viewId,
        workspaceId: this.activeWorkspaceId,
      };
    },

    /**
     * Create/Delete/View Settings.
     */
    async showViewSettingsDialog(item, initialControlsTab) {
      const res = await this.loadViewSettingsData(item, initialControlsTab);
      if (res) this.showSettingsDialog = true;
    },
    async showCopyPopup() {
      this.spinner(true);
      try {
        this.showCopyDialog = true;
      } catch (err) {
        this.handleError(err);
      } finally {
        this.spinner(false);
      }
    },
    async onCopy() {
      const item = await this.copyView();
      this.showViewSettingsDialog(item);
      await this.fetchRolesForView(item);
    },
    async deleteSelectedView(view) {
      const confirmDismissal = await this.$openConfirm({
        text: `Are you sure you want to delete "${view.name}?"`,
      });
      if (!confirmDismissal) return;

      this.deleteView(view);
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .v-card__title {
  display: flex !important;
  justify-content: space-between;
}

.view-manager-wrapper {
  font-family: Poppins;
  border: 1px solid var(--v-darkGrey-base);
  background: white;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.view-manager-row {
  padding: 2rem 0 2rem 0.5rem;
  display: grid;
  place-items: center;
  grid-template-columns: 1fr 5rem 1fr 1fr;
  gap: 2rem;
  font-size: 0.875rem;
  border-bottom: 1px solid var(--v-darkGrey-base);
  text-align: center;

  &:last-child {
    border-bottom: none;
  }

  ::v-deep .vlist-item {
    &::after {
      display: none;
    }
  }

  &__view-name {
    padding-left: 2rem;
    justify-self: start;
    text-align: left;
  }
}
</style>